import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  cloneElement,
} from 'react';
import PropTypes from 'prop-types';
import HeaderStyles from './Header.styled';
import { useLocation } from 'react-router-dom';
import MenuToggle from '../Shared/Icons/menuToggle';
import Alerts from '../Alerts/Alerts';
import { useAppContext } from '../../contexts/AppContext';
import EnergyRightMobileHeader from './EnergyRight/MobileHeader';
import EnergyRightPreHeader from './EnergyRight/PreHeader';
import EnergyRightMobileMenu from './EnergyRight/MobileMenu';
import EnergyRightDesktopHeader from './EnergyRight/DesktopHeader';
import HUGMobileHeader from './HomeUpgradeLoan/MobileHeader';
import HUGPreHeader from './HomeUpgradeLoan/PreHeader';
import HUGMobileMenu from './HomeUpgradeLoan/MobileMenu';
import HUGDesktopHeader from './HomeUpgradeLoan/DesktopHeader';
import DirectLendingHeader from './DirectLending/DesktopHeader';
import DirectLendingMobile from './DirectLending/MobileHeader';
import DirectLendingMobileMenu from './DirectLending/MobileMenu';
import DefaultMobileHeader from './Default/MobileHeader';
import DefaultDesktopHeader from './Default/DesktopHeader';
import PPCDesktopHeader from './PPC/DesktopHeader';
import PPCMobileHeader from './PPC/MobileHeader';
import PromoLPTemplate from './PromoLPTemplate/DesktopHeader';
import BusCardTemplate from './BusCardTemplate/DesktopHeader';
import Contact from './Contact';
import { useTheme } from 'styled-components';
import UpdatedDesktopHeader from './Default/UpdatedDesktopHeader/UpdatedDesktopHeader.tsx';
import MyAccountIcon from '../Shared/Icons/MyAccountIcon';
import Link from '../Shared/Link';
import { getBaseExpressAppUrl } from '../../util/config';
import UpdatedMobileMenu from './Default/UpdatedMobileMenu/UpdatedMobileMenu';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';

const defaultHUGContractor = {
  mobileHeader: (
    <DefaultMobileHeader>
      <HUGMobileHeader isContractor />
    </DefaultMobileHeader>
  ),
  mobileMenu: <HUGMobileMenu isContractor />,
  preHeader: <HUGPreHeader isContractor />,
  desktopHeader: (
    <DefaultDesktopHeader hideNavigation hideButtons>
      <HUGDesktopHeader isContractor />
    </DefaultDesktopHeader>
  ),
};

const defaultHUGHomeowner = {
  mobileHeader: (
    <DefaultMobileHeader>
      <HUGMobileHeader />
    </DefaultMobileHeader>
  ),
  mobileMenu: <HUGMobileMenu />,
  preHeader: <HUGPreHeader />,
  desktopHeader: (
    <DefaultDesktopHeader hideNavigation hideButtons>
      <HUGDesktopHeader />
    </DefaultDesktopHeader>
  ),
};

const homeLoanLight = {
  mobileHeader: (
    <DefaultMobileHeader isLandingPage>
      <Contact />
    </DefaultMobileHeader>
  ),
  mobileMenu: false,
  desktopHeader: (
    <DefaultDesktopHeader hideNavigation hideButtons isLandingPage>
      <Contact />
    </DefaultDesktopHeader>
  ),
  preHeader: false,
};

const homeLoanDark = {
  mobileHeader: (
    <DefaultMobileHeader isDark isLandingPage>
      <Contact isDark />
    </DefaultMobileHeader>
  ),
  mobileMenu: false,
  desktopHeader: (
    <DefaultDesktopHeader hideNavigation hideButtons isDark isLandingPage>
      <Contact isDark />
    </DefaultDesktopHeader>
  ),
  preHeader: false,
  isDark: true,
};

const energyRight = {
  mobileHeader: (
    <EnergyRightMobileHeader>
      <Contact isDark />
    </EnergyRightMobileHeader>
  ),
  mobileMenu: (
    <EnergyRightMobileMenu
      phoneNumber='1-888-893-8792'
      email='EnergyRightLoans@vmf.com'
    />
  ),
  desktopHeader: <EnergyRightDesktopHeader hideNavigation hideButtons />,
  preHeader: (
    <EnergyRightPreHeader
      phoneNumber='1-888-893-8792'
      email='EnergyRightLoans@vmf.com'
    />
  ),
};

const ppcHeader = {
  mobileHeader: (
    <DefaultMobileHeader headerLogoUrl='/?utm_medium=ppc'>
      <PPCMobileHeader />
    </DefaultMobileHeader>
  ),
  mobileMenu: false,
  desktopHeader: (
    <DefaultDesktopHeader
      hideNavigation
      hideButtons
      headerLogoUrl='/?utm_medium=ppc'
    >
      <PPCDesktopHeader />
    </DefaultDesktopHeader>
  ),
  preHeader: false,
  isDark: false,
};
const defaultDirectLending = {
  mobileHeader: <DirectLendingMobile />,
  mobileMenu: <DirectLendingMobileMenu />,
  preHeader: <Alerts />,
  desktopHeader: <DirectLendingHeader />,
};
const defaultPromoLPTemplate = {
  desktopHeader: <PromoLPTemplate />,
  mobileHeader: <PromoLPTemplate />,
};

// The Apply Today button on the Used Home Loan page (/manufactured-home-loans/used-home-loan/) needs a slightly different link.
const usedHomeLoanButtonLinks = [
  {
    title: 'Apply Today',
    to: '/applynow/welcome?ReferringTM=null',
    gtmClassname: 'gtm_apply_button_navbar',
  },
];

const defaultUsedHomeLoan = {
  desktopHeader: <UpdatedDesktopHeader buttonLinks={usedHomeLoanButtonLinks} />,
  mobileHeader: <DefaultMobileHeader />,
  mobileMenu: <UpdatedMobileMenu buttonLinks={usedHomeLoanButtonLinks} />,
};

const configs = {
  '/contractor-enroll/': defaultHUGContractor,
  '/contractor-enroll/contact-us/': defaultHUGContractor,
  '/contractor-enroll/thank-you/': defaultHUGContractor,
  '/ce/': defaultHUGContractor,
  '/ce/contact-us/': defaultHUGContractor,
  '/ce/thank-you/': defaultHUGContractor,
  '/home-upgrade-loan/': defaultHUGHomeowner,
  '/home-upgrade-loan/availability/': defaultHUGHomeowner,
  '/applynow/welcome/': homeLoanLight,
  '/applynow/welcome2/': homeLoanDark,
  '/pages': ppcHeader,
  '/direct-lending/': defaultDirectLending,
  '/tva-energyright-loan/': energyRight,
  '/promo': defaultPromoLPTemplate,
  '/used-home-loan': defaultUsedHomeLoan,

  default: {
    mobileHeader: <DefaultMobileHeader />,
    mobileMenu: <UpdatedMobileMenu />,
    desktopHeader: <UpdatedDesktopHeader />,
    preHeader: <Alerts />,
  },
};

const myAccountLinks = [
  {
    title: 'Sign In',
    link: getBaseExpressAppUrl() + '/portal/login/',
    gtmClassname: 'gtm_sign_in_navbar',
  },
  {
    title: 'Make a Payment',
    link: getBaseExpressAppUrl() + '/portal/express-pay',
    gtmClassname: 'gtm_make_payment_navbar',
  },
  {
    title: 'Payment Options',
    link: '/resources/payment-options/',
    gtmClassname: 'gtm_payment_options_navbar',
  },
  {
    title: 'Documents & Forms',
    link: '/resources/documents-forms/',
    gtmClassname: 'gtm_documents_navbar',
  },
];

function Header() {
  const theme = useTheme();
  const [{ scrollDirection, headerHeight }, dispatch] = useAppContext();
  // Manage the nav state
  const [navOpen, setNavOpen] = useState(false);
  const [resizeTimeout, setResizeTimeout] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(false);
  const toggleMenu = () => {
    setNavOpen(!navOpen);
    setActiveDropdown(false);
  };
  const toggleAccountDropdown = () => {
    setActiveDropdown(!activeDropdown);
    setNavOpen(false);
  };
  // Use close menu for when user clicks on current route
  const headerRef = useRef();
  const location = useLocation();

  // This cleans the pathname to match the keys in the configs object and handles special cases.
  const cleanPathName = useMemo(() => {
    const path = location.pathname;
    const pathSegments = path.split('/');

    // Ensure path ends with '/'
    const normalizedPath = path.slice(-1) !== '/' ? `${path}/` : path;

    // Check for special cases first
    if (pathSegments[1] === 'pages') return '/pages';
    if (pathSegments[1] === 'promo') return '/promo';
    if (pathSegments[3] === 'team') return '/direct-lending/team';
    if (pathSegments[2] === 'used-home-loan') return '/used-home-loan';

    return normalizedPath;
  }, [location]);

  // This selects a menu configuration for any of the loan officer 'team' pages (/manufactured-home-loans/:teamName/team/:officerUID)
  // The menu configuration for lending team pages (includes Loan Officer detail pages) uses a different Apply Today link
  // which is set in the AppContext as the headerLink.
  // The defaultBusCardHeader had to be defined here and then added to the config because it needs access to the AppContext to get the headerLink.
  const [{ headerLink }] = useAppContext();
  const businessCardButtonLinks = [
    {
      title: 'Apply Today',
      to: headerLink,
      gtmClassname: 'gtm_apply_button_navbar',
    },
  ];

  const defaultBusCardHeader = {
    desktopHeader: (
      <UpdatedDesktopHeader buttonLinks={businessCardButtonLinks} />
    ),
    mobileHeader: <DefaultMobileHeader />,
    mobileMenu: <UpdatedMobileMenu buttonLinks={businessCardButtonLinks} />,
  };

  // Add the business card header to the configs object
  configs['/direct-lending/team'] = defaultBusCardHeader;

  // Select an appropriate configuration (or default) from the configs object
  const config = configs[cleanPathName] || configs.default;

  useEffect(() => {
    if (navOpen) {
      setNavOpen(false);
      setActiveDropdown(false);
    }
  }, [location]);

  useEffect(() => {
    setTimeout(() => {
      dispatch({
        type: 'UPDATE_HEADER_HEIGHT',
        headerHeight: headerRef.current.clientHeight - 1,
      });
    }, 100);
  }, [window.location.pathname]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_HEADER_HEIGHT',
      headerHeight: headerRef.current.clientHeight - 1,
    });
  }, [headerRef]);

  useEffect(() => {
    const handleWindowResize = e => {
      if (resizeTimeout) {
        setResizeTimeout(clearTimeout(resizeTimeout));
      }

      setResizeTimeout(
        setTimeout(() => {
          dispatch({
            type: 'UPDATE_HEADER_HEIGHT',
            headerHeight: headerRef.current.clientHeight,
          });
        }, 500)
      );
    };
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  const navContainerRef = useRef();
  const signInRef = useRef();
  const hamburgerRef = useRef();

  const handleOutsideClicksSignIn = e => {
    if (activeDropdown) {
      const signInArea = signInRef.current;

      // Check if click was outside area
      const clickedOutsideSignIn =
        !signInArea ||
        (e.target !== signInArea && !signInArea.contains(e.target));

      // // If click was outside area, close the sign in dropdown
      if (clickedOutsideSignIn) {
        setActiveDropdown(false);
        setNavOpen(false);
      }
    }
  };

  const handleOutsideClicksNavItems = e => {
    // was activedropdown
    if (navOpen) {
      const navArea = navContainerRef.current;
      const hamburgerArea = hamburgerRef.current;

      // Check if click was outside area
      const clickedOutsideNav =
        !navArea || (e.target !== navArea && !navArea.contains(e.target));
      const clickedOutsideHamburger =
        !hamburgerArea ||
        (e.target !== hamburgerArea && !hamburgerArea.contains(e.target));

      // If click was outside both areas, close the mobile dropdown.
      // This prevents the hamburger from calling the toggle function when the menu is open
      // and the user clicks on the hamburger icon again.
      if (clickedOutsideNav && clickedOutsideHamburger) {
        setNavOpen(false);
      }
    }
  };

  if (typeof document !== 'undefined') {
    useOnClickOutside(signInRef, handleOutsideClicksSignIn);
    useOnClickOutside(navContainerRef, handleOutsideClicksNavItems);
  }

  return (
    <>
      <HeaderStyles
        ref={headerRef}
        scrollDirection={scrollDirection}
        isOpen={navOpen}
        isDark={config?.isDark}
        $ignoreShadow={theme.config.header.ignoreShadows.some(
          s => cleanPathName.slice(0, 6) === s
        )}
        overrideBreakpoint={
          location.pathname.split('/')[2] === 'team'
            ? theme.breakpoints.tablet
            : false
        }
        activeDropdown={activeDropdown}
      >
        {config?.preHeader}
        <div className='inner'>
          <div className='mobile_header'>
            {config?.mobileHeader}
            {config?.mobileMenu && (
              <>
                <div
                  className='account-icon gtm_myaccount_navbar'
                  onClick={toggleAccountDropdown}
                >
                  <MyAccountIcon />
                  <div
                    className={`sign-in-dropdown ${activeDropdown ? 'active' : ''}`}
                    ref={signInRef}
                  >
                    {myAccountLinks.map((link, linkIndex) => (
                      <Link
                        key={linkIndex}
                        to={link.link}
                        className={`sign-in-dropdown-link ${link.gtmClassname ? link.gtmClassname : ''}`}
                        includeArrowIcon={false}
                      >
                        {link.title}
                      </Link>
                    ))}
                  </div>
                </div>
                <button
                  className='mobile_toggle'
                  type='button'
                  aria-label='Toggle Menu'
                  onClick={toggleMenu}
                  ref={hamburgerRef}
                >
                  <MenuToggle isOpen={navOpen} />
                </button>
              </>
            )}
          </div>
          <div className='desktop_header'>{config?.desktopHeader}</div>
          {config?.mobileMenu && (
            <div className='mobile_menu' ref={navContainerRef}>
              {cloneElement(config?.mobileMenu, {
                closeMenu: () => setNavOpen(false),
                isOpen: navOpen,
              })}
            </div>
          )}
        </div>
      </HeaderStyles>
      <div
        style={{
          height: '300px',
          maxHeight: `${headerHeight}px`,
          transition: 'all .4s ease-in-out',
        }}
      />
    </>
  );
}

Header.propTypes = {
  alerts: PropTypes.arrayOf(PropTypes.object),
  closeAlert: PropTypes.func,
  hidden: PropTypes.bool,
};

export default Header;
